import React, { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NotificationContext from "../context/NotificationContext";
import {
  FEE_PROOF_ACCEPT,
  validateFeeProofFile,
} from "../utils/feeProofUpload";
import { getFeeDueMessaging } from "../utils/dateFormat";
import {
  formatPkrAmount,
  parseFeeAmount,
  resolveStudentFeeState,
} from "../utils/studentFeeState";
import * as registrationsApi from "../api/registrations";
import "./PendingFeePopup.css";

function formatCourseCharge(course) {
  if (course?.chargedRemaining != null) {
    return formatPkrAmount(course.chargedRemaining);
  }
  return formatPkrAmount(parseFeeAmount(course?.fee));
}

export default function PendingFeePopup({ regObj, onClose, onResolved }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const notificationCtx = useContext(NotificationContext);

  const [registration, setRegistration] = useState(regObj);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [droppingId, setDroppingId] = useState(null);

  useEffect(() => {
    setRegistration(regObj);
  }, [regObj]);

  const feeState = useMemo(
    () => resolveStudentFeeState(registration),
    [registration],
  );

  const chargedCourses = feeState.feeCourses;
  // Always the ledger payable (owedTotal) / pending catalog — never gross remainders.
  const billTotal = Number(feeState.outstanding) || 0;

  const canDropPending =
    feeState.source === "pending_enrollment" ||
    feeState.source === "pending_and_ledger";
  const amountLabel = feeState.outstandingLabel;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }

      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (!feeState.needsPayment) {
      onResolved?.();
    }
  }, [feeState.needsPayment, onResolved]);

  const handleDrop = (course) => {
    notificationCtx.show({
      message: `Drop "${course.title}" from your registration?`,
      onConfirm: async () => {
        setDroppingId(course.id);
        try {
          await registrationsApi.dropByStudent(registration.id, {
            courseId: course.id,
          });
          notificationCtx.show({
            message: "Course dropped successfully",
            stat: "success",
          });
          setRegistration((prev) => ({
            ...prev,
            courses: (prev.courses || []).map((item) =>
              item.id === course.id
                ? { ...item, status: "dropped without refund" }
                : item,
            ),
          }));
        } catch (err) {
          console.error(err);
          notificationCtx.show({
            message:
              err?.response?.data?.message ||
              "An error occurred while dropping the course",
            stat: "error",
          });
        } finally {
          setDroppingId(null);
        }
      },
    });
  };

  const handleUpload = async () => {
    if (uploading || !registration) return;
    const validationError = validateFeeProofFile(file);
    if (validationError) {
      notificationCtx.show({ message: validationError, stat: "error" });
      return;
    }

    setUploading(true);
    const registrationId = registration.id || registration.registrationId;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("registrationId", registrationId);

    try {
      await registrationsApi.uploadFeeImage(registrationId, formData);
      notificationCtx.show({
        message: "Upload successful",
        stat: "success",
      });
      setFile(null);
      setRegistration((prev) => ({
        ...prev,
        courses: (prev.courses || []).map((course) =>
          String(course.status || "").toLowerCase() === "pending"
            ? { ...course, status: "enrolled" }
            : course,
        ),
      }));
      onResolved?.();
    } catch (err) {
      notificationCtx.show({
        message: err?.response?.data?.message || "Upload failed",
        stat: "error",
      });
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  if (!registration) {
    return (
      <div className="pending-fee-overlay" role="presentation">
        <div className="pending-fee-dialog" role="dialog" aria-modal="true">
          <div className="pending-fee-main">
            <p className="pending-fee-empty">Unable to load fee details.</p>
          </div>
        </div>
      </div>
    );
  }

  const dueMessaging = getFeeDueMessaging(registration.duedate);

  const payCopy =
    registration.city === "Lahore" ? (
      <>
        Pay at the <strong>Zainematics office</strong> (216, Street 10, Y
        Block, Phase 3 DHA Lahore), then upload your receipt.
      </>
    ) : (
      <>
        Pay <strong>online</strong>, then upload a screenshot of your payment
        proof.
      </>
    );

  return (
    <div
      className="pending-fee-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="pending-fee-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="pending-fee-dialog__top">
          <div>
            <p className="pending-fee-dialog__eyebrow">Action needed</p>
            <h2 id={titleId} className="pending-fee-dialog__title">
              Complete your fee payment
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="pending-fee-dialog__close"
            onClick={onClose}
            aria-label="Close fee pending dialog"
          >
            ×
          </button>
        </div>

        <div className="pending-fee-dialog__layout">
          <aside className="pending-fee-aside">
            <div>
              <p className="pending-fee-aside__amount-label">Amount due</p>
              <p className="pending-fee-aside__amount">{amountLabel}</p>
              <div className="pending-fee-aside__meta">
                <span className="pending-fee-pill pending-fee-pill--status">
                  {feeState.paymentStatusLabel}
                </span>
                <span
                  className={`pending-fee-pill pending-fee-pill--due pending-fee-pill--${dueMessaging.tone}`}
                >
                  {dueMessaging.pillLabel}
                </span>
              </div>
            </div>

            <div
              className={`pending-fee-next-step pending-fee-next-step--${dueMessaging.tone}`}
            >
              <p className="pending-fee-next-step__label">
                {dueMessaging.nextStepLabel}
              </p>
              <p className="pending-fee-next-step__copy">{payCopy}</p>
            </div>

            {feeState.needsPayment ? (
              <div className="pending-fee-upload-card">
                <h3 className="pending-fee-upload-card__title">Upload proof</h3>
                <p className="pending-fee-upload-card__hint">
                  Attach your receipt or payment screenshot to continue.
                </p>
                <label
                  className="pending-fee-upload__label"
                  htmlFor="pending-fee-proof"
                >
                  Payment proof file
                </label>
                <input
                  id="pending-fee-proof"
                  className="pending-fee-upload__input"
                  type="file"
                  accept={FEE_PROOF_ACCEPT}
                  onChange={(e) => {
                    const next = e.target.files?.[0] || null;
                    if (!next) {
                      setFile(null);
                      return;
                    }
                    const validationError = validateFeeProofFile(next);
                    if (validationError) {
                      notificationCtx.show({
                        message: validationError,
                        stat: "error",
                      });
                      e.target.value = "";
                      setFile(null);
                      return;
                    }
                    setFile(next);
                  }}
                />
                <button
                  type="button"
                  className="pending-fee-upload__btn"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                >
                  {uploading ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        aria-hidden="true"
                      />
                      Uploading…
                    </>
                  ) : (
                    "Upload proof"
                  )}
                </button>
                {file ? (
                  <p className="pending-fee-upload__filename">
                    Selected: {file.name}
                  </p>
                ) : null}
                <p className="pending-fee-upload-card__note">
                  Already uploaded? Wait for admin to update your status.
                </p>
              </div>
            ) : null}
          </aside>

          <div className="pending-fee-main">
            <section>
              <div className="pending-fee-block__head">
                <h3 className="pending-fee-block__title">
                  {feeState.source === "ledger"
                    ? "Charged enrollments"
                    : "Awaiting payment"}
                </h3>
                <span className="pending-fee-block__count">
                  {chargedCourses.length} course
                  {chargedCourses.length === 1 ? "" : "s"}
                </span>
              </div>

              {chargedCourses.length === 0 ? (
                <p className="pending-fee-empty">
                  No enrollments are currently being charged.
                </p>
              ) : (
                <>
                  <ul className="pending-fee-rows">
                    {chargedCourses.map((course) => (
                      <li
                        key={course.id}
                        className={`pending-fee-row pending-fee-row--with-months${
                          canDropPending ? "" : " pending-fee-row--static"
                        }`}
                      >
                        <div className="pending-fee-row__top">
                          <p className="pending-fee-row__title">
                            {course.title}
                          </p>
                          <span className="pending-fee-row__fee">
                            {formatCourseCharge(course)}
                          </span>
                          {canDropPending ? (
                            <button
                              type="button"
                              className="pending-fee-row__drop"
                              onClick={() => handleDrop(course)}
                              disabled={droppingId === course.id}
                              aria-label={`Drop ${course.title}`}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                aria-hidden="true"
                              />
                            </button>
                          ) : null}
                        </div>
                        {course.monthCharges?.length ? (
                          <ul className="pending-fee-month-split">
                            {course.monthCharges.map((month) => (
                              <li
                                key={month.key}
                                className="pending-fee-month-split__item"
                              >
                                <span className="pending-fee-month-split__label">
                                  {month.label}
                                </span>
                                <span className="pending-fee-month-split__amt">
                                  {formatPkrAmount(month.remaining)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="pending-fee-month-split__fallback">
                            Charged for current enrollment fee
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>

                  {feeState.dueMonths?.length ? (
                    <div className="pending-fee-due-months">
                      <div className="pending-fee-block__head">
                        <h3 className="pending-fee-block__title">
                          Due by month
                        </h3>
                        <span className="pending-fee-block__count">
                          {feeState.dueMonths.length} month
                          {feeState.dueMonths.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <ul className="pending-fee-due-months__list">
                        {feeState.dueMonths.map((month) => (
                          <li key={month.key}>
                            <span>{month.label}</span>
                            <strong>{formatPkrAmount(month.remaining)}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="pending-fee-block__footer">
                    <span>Amount due on these courses</span>
                    <span>{formatPkrAmount(billTotal)}</span>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
